import { InputAdornment, TextField, Tooltip } from "@mui/material";
import { FunctionComponent, useEffect, useState } from "react";
import styles from "./seachField.module.css";
import { ClearIcon } from "@mui/x-date-pickers/icons";
import SearchIcon from "@mui/icons-material/Search";
import SearchHelp, { SearchHelpVariant } from "./SearchHelp";

type PropsType = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onFocus?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  // Optional escape hatch for consumers that need to imperatively focus the
  // underlying <input> (e.g. GlobalSearch restoring focus after its results
  // modal closes). Unused by existing call sites.
  inputRef?: React.Ref<HTMLInputElement>;
  // Which wording the Help tooltip uses. "none" hides it - GlobalSearch does
  // that, because its results modal carries the full rules already.
  help?: SearchHelpVariant | "none";
};

const SearchField: FunctionComponent<PropsType> = ({
  value,
  onChange,
  placeholder,
  onFocus,
  onKeyDown,
  inputRef,
  help = "table",
}) => {
  // What is in the box. Committed to the owner on Enter, never before -
  // this used to fire onChange 2s after the last keystroke, which meant
  // every intermediate word ran a query, and it disagreed with the
  // customer overview, which had its own box that searched only on Enter.
  // One behaviour everywhere: type, press Enter.
  const [draft, setDraft] = useState<string>(value);

  // The owner can still change `value` out from under the box - EE-222's
  // "See all N" seeds the order/customer search term from a query param
  // after this component has mounted. Without this the term filters the
  // table while the visible box shows stale or empty text.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const commit = (next: string) => {
    setDraft(next);
    onChange(next);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    // A consumer that handled Enter itself calls preventDefault - GlobalSearch
    // does, because Enter there opens the results modal rather than filtering
    // a table in place.
    if (event.defaultPrevented) return;
    if (event.key === "Enter") commit(draft);
    if (event.key === "Escape") commit("");
  };

  // Typed but not yet searched. Worth showing, or an uncommitted term reads
  // as a filter that silently does nothing. Deliberately an adornment rather
  // than helperText: this field sits in fixed-height page headers, and
  // helperText changes the field's height.
  const pending = draft !== value;

  return (
    <div className={styles.wrapper}>
      <TextField
        size="small"
        className={styles.searchField}
        value={draft || ""}
        onChange={(event) => setDraft(event.target.value)}
        placeholder={placeholder ?? `Search - press Enter`}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        inputRef={inputRef}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {pending && (
                <Tooltip title="Press Enter to search">
                  <span className={styles.pendingHint}>&#9166;</span>
                </Tooltip>
              )}
              {draft && (
                <ClearIcon
                  onClick={() => commit("")}
                  style={{ cursor: "pointer" }}
                />
              )}
            </InputAdornment>
          ),
        }}
      />
      {help !== "none" && <SearchHelp variant={help} />}
    </div>
  );
};

export default SearchField;
